import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { usePark } from "@/contexts/ParkContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader, StatusBadge, formatCurrency, formatDate } from "@/components/shared";
import { useAuth } from "@/contexts/AuthContext";
import {
  Users, ParkingSquare, CalendarRange, Wallet, ArrowDownToLine, ArrowUpFromLine, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, value, hint, testId }) {
  return (
    <Card className="border-[#EAEAE5] shadow-none hover:shadow-sm transition-shadow" data-testid={testId}>
      <CardContent className="p-3 sm:p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#737A76] mb-1.5 sm:mb-2">{label}</div>
            <div className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#1A211D]" style={{ fontFamily: "Outfit" }}>{value}</div>
            {hint && <div className="text-[11px] sm:text-xs text-[#737A76] mt-1">{hint}</div>}
          </div>
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-[#F0F0EC] flex items-center justify-center text-[#284B3B] shrink-0">
            <Icon size={16} strokeWidth={1.8} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { currentPark } = usePark();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [checkIns, setCheckIns] = useState([]);
  const [checkOuts, setCheckOuts] = useState([]);

  const load = async () => {
    try {
      const [s, ci, co] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/dashboard/check-ins"),
        api.get("/dashboard/check-outs"),
      ]);
      setStats(s.data);
      setCheckIns(ci.data);
      setCheckOuts(co.data);
    } catch (e) {
      toast.error("Failed to load dashboard");
    }
  };

  useEffect(() => { if (currentPark?.id) load(); }, [currentPark?.id]);

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Control Center"
        subtitle="A real-time snapshot of your park."
        testId="dashboard-header"
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <StatCard icon={ParkingSquare} label="Occupancy" value={`${stats?.occupancy_pct ?? 0}%`} hint={`${stats?.active_reservations ?? 0} of ${stats?.total_lots ?? 0} lots`} testId="stat-occupancy" />
        <StatCard icon={CalendarRange} label="Upcoming reservations" value={stats?.upcoming_reservations ?? 0} hint="Across all lease types" testId="stat-upcoming" />
        <StatCard icon={Wallet} label="Outstanding" value={formatCurrency(stats?.outstanding_balance)} hint="All open invoices" testId="stat-outstanding" />
        <StatCard icon={TrendingUp} label="Revenue this month" value={formatCurrency(stats?.revenue_mtd)} hint="Payments received" testId="stat-revenue" />
      </div>

      <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <StatCard icon={Users} label="Tenants" value={stats?.total_tenants ?? 0} testId="stat-tenants" />
        <StatCard icon={ArrowDownToLine} label="Check-ins today" value={stats?.check_ins_today ?? 0} testId="stat-checkins-today" />
        <StatCard icon={ArrowUpFromLine} label="Check-outs today" value={stats?.check_outs_today ?? 0} testId="stat-checkouts-today" />
      </div>

      {/* Today's lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-[#EAEAE5] shadow-none" data-testid="todays-checkins-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium" style={{ fontFamily: "Outfit" }}>Today’s check-ins</CardTitle>
              <span className="text-[10px] uppercase tracking-wider text-[#737A76]">{checkIns.length}</span>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {checkIns.length === 0 ? (
              <div className="text-sm text-[#737A76] py-8 text-center">No arrivals scheduled for today.</div>
            ) : (
              <table className="w-full text-sm pd-table">
                <thead>
                  <tr className="text-left border-b border-[#EAEAE5]">
                    <th className="py-2">Guest</th><th>Lot</th><th>Type</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {checkIns.map(({ reservation, tenant, lot }) => (
                    <tr key={reservation.id} className="border-b border-[#F0F0EC] last:border-0">
                      <td className="py-2.5">{tenant?.first_name} {tenant?.last_name}</td>
                      <td>#{lot?.number}</td>
                      <td><StatusBadge status={reservation.lease_type} /></td>
                      <td className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/reservations/${reservation.id}`)}>View</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card className="border-[#EAEAE5] shadow-none" data-testid="todays-checkouts-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium" style={{ fontFamily: "Outfit" }}>Today’s check-outs</CardTitle>
              <span className="text-[10px] uppercase tracking-wider text-[#737A76]">{checkOuts.length}</span>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {checkOuts.length === 0 ? (
              <div className="text-sm text-[#737A76] py-8 text-center">No departures scheduled for today.</div>
            ) : (
              <table className="w-full text-sm pd-table">
                <thead>
                  <tr className="text-left border-b border-[#EAEAE5]">
                    <th className="py-2">Guest</th><th>Lot</th><th>Balance</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {checkOuts.map(({ reservation, tenant, lot }) => (
                    <tr key={reservation.id} className="border-b border-[#F0F0EC] last:border-0">
                      <td className="py-2.5">{tenant?.first_name} {tenant?.last_name}</td>
                      <td>#{lot?.number}</td>
                      <td>{formatCurrency(reservation.total - (reservation.deposit || 0))}</td>
                      <td className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/reservations/${reservation.id}`)}>View</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
